import { CurrencyCodes } from '@onevesthq/ov-enums';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { TFunction } from 'i18next';
import type { PortfolioAdvisorDecisionInsight } from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { isFundingSegregationNotApplied } from '../audit/decisionPredicates';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';
import {
  AdvisorSituationKey,
  OptimizerDecisionStage,
} from './advisorSituationKeys';
import {
  CASH_DEPLOY_HOLDINGS_PATTERN,
  CASH_RAISE_HOLDINGS_PATTERN,
  FUNDING_SEGREGATION_APPLIED_PATTERN,
  isCashDeployAction,
  isCashRaiseAction,
  LIQUIDATION_SUMMARY_PATTERN,
  STRUCTURAL_FUNDING_DRIFT_PATTERN,
  ZERO_CASH_MODE_PATTERN,
  ZERO_DEPLOY_CASH_MODE_PATTERN,
} from './optimizerDecisionActionPatterns';
import { resolveCashModeInsightSource } from './resolveCashModeInsightSource';

const SUB_ACCOUNT_ID_PATTERN = /\b[a-f0-9]{24}\b/gi;

const resolveSubAccountName = (
  subAccountId: string,
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
  audit?: OptimizerAuditV2Record,
) => {
  const fromReport = subAccountsById.get(subAccountId);
  if (fromReport?.name) {
    return fromReport.name;
  }

  const fromAudit = (audit?.subAccountResults ?? []).find(
    (result) => result.subAccountId === subAccountId,
  );

  if (fromAudit?.accountType) {
    return formatAuditAccountType(fromAudit.accountType);
  }

  return subAccountId;
};

const replaceSubAccountIds = (
  action: string,
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
  audit?: OptimizerAuditV2Record,
) =>
  action.replace(SUB_ACCOUNT_ID_PATTERN, (subAccountId) =>
    resolveSubAccountName(subAccountId, subAccountsById, audit),
  );

export const isZeroCashModeApplication = (action: string): boolean =>
  ZERO_CASH_MODE_PATTERN.test(action);

export const isZeroDeployCashModeApplication = (action: string): boolean =>
  ZERO_DEPLOY_CASH_MODE_PATTERN.test(action);

export const resolveAdvisorSituationKey = (decision: {
  stage: string;
  action: string;
}): string => {
  if (decision.stage === OptimizerDecisionStage.FundingSegregation) {
    if (isFundingSegregationNotApplied(decision)) {
      return decision.stage;
    }

    return AdvisorSituationKey.MultiCurrency;
  }

  if (decision.stage !== OptimizerDecisionStage.RebalanceMode) {
    return decision.stage;
  }

  if (isZeroCashModeApplication(decision.action)) {
    return AdvisorSituationKey.MultiCurrency;
  }

  if (isCashRaiseAction(decision.action)) {
    return AdvisorSituationKey.CashRaise;
  }

  if (isCashDeployAction(decision.action)) {
    return AdvisorSituationKey.CashDeploy;
  }

  return AdvisorSituationKey.CashMode;
};

export const formatAdvisorDecisionStage = (
  decision: Pick<PortfolioAdvisorDecisionInsight, 'stage' | 'action'>,
  t: TFunction,
): string => {
  switch (resolveAdvisorSituationKey(decision)) {
    case AdvisorSituationKey.CashRaise:
      return t('bulkRebalance:insights.decisionStage.cashRaise');
    case AdvisorSituationKey.CashDeploy:
      return t('bulkRebalance:insights.decisionStage.cashDeploy');
    case AdvisorSituationKey.CashMode:
      return t('bulkRebalance:insights.decisionStage.cash');
    case AdvisorSituationKey.MultiCurrency:
      return t('bulkRebalance:insights.decisionStage.multiCurrency');
    default:
      return decision.stage
        .split('_')
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(' ');
  }
};

export type FormatAdvisorDecisionContext = {
  diffRows?: WeightDiffDetailRow[];
  usedCashModeSubAccountIds?: Set<string>;
};

export const formatAdvisorDecisionAction = (
  decision: PortfolioAdvisorDecisionInsight,
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
  audit: OptimizerAuditV2Record | undefined,
  t: TFunction,
  context: FormatAdvisorDecisionContext = {},
): string => {
  const action = replaceSubAccountIds(decision.action, subAccountsById, audit);

  switch (decision.stage) {
    case 'LIQUIDATION': {
      const normalized = action.replace(/^Sub-account\s+/i, '');
      const match = normalized.match(LIQUIDATION_SUMMARY_PATTERN);

      if (!match) {
        return action;
      }

      const [, accountName, reason] = match;
      const friendlyReason = reason.includes('pending withdraw')
        ? t('bulkRebalance:insights.liquidationWithdraw')
        : t('bulkRebalance:insights.liquidationCashTarget');

      return t('bulkRebalance:insights.liquidationSummary', {
        account: accountName,
        reason: friendlyReason,
      });
    }

    case OptimizerDecisionStage.TaxLocation: {
      const appliedMatch = action.match(
        /Tax-location penalty applied:.*(\d+) sub-accounts x (\d+) holdings/i,
      );

      if (appliedMatch) {
        return t('bulkRebalance:insights.taxLocationApplied', {
          subAccountCount: appliedMatch[1],
          holdingCount: appliedMatch[2],
        });
      }

      if (action.startsWith('Tax-location skipped:')) {
        const reason = action.replace('Tax-location skipped:', '').trim();
        return t('bulkRebalance:insights.taxLocationSkipped', { reason });
      }

      if (action.includes('degenerate')) {
        return t('bulkRebalance:insights.taxLocationDegenerate');
      }

      return action;
    }

    case 'FX_DETECTION': {
      const fxMatch = action.match(
        /FX exposure detected: (\d+) holding\(s\) in (.+?), estimated cost ~(\d+)bps/i,
      );

      if (fxMatch) {
        return t('bulkRebalance:insights.fxExposure', {
          holdingCount: fxMatch[1],
          currencies: fxMatch[2],
          bps: fxMatch[3],
        });
      }

      return action;
    }

    case 'SWITCH_DETECTION':
      return action.replace(
        /^(\d+) switch-eligible pair\(s\) detected:/i,
        t('bulkRebalance:insights.switchOpportunitiesPrefix'),
      );

    case 'COVARIANCE':
      if (action.includes('fell back to identity covariance')) {
        return t('bulkRebalance:insights.covarianceFallback');
      }

      return action;

    case 'QP_SOLVE':
      if (action.startsWith('QP failed:')) {
        return t('bulkRebalance:insights.qpFailed', {
          reason: action.replace('QP failed:', '').trim(),
        });
      }

      return action;

    case OptimizerDecisionStage.FundingSegregation: {
      if (FUNDING_SEGREGATION_APPLIED_PATTERN.test(action)) {
        return t('bulkRebalance:insights.multiCurrencyCashStays');
      }
      if (/skipped/i.test(action)) {
        return t('bulkRebalance:insights.multiCurrencyCouldNotSeparate');
      }
      if (/conservation violated/i.test(action)) {
        return t('bulkRebalance:insights.multiCurrencyMixedFunding');
      }
      if (STRUCTURAL_FUNDING_DRIFT_PATTERN.test(action)) {
        return t('bulkRebalance:insights.multiCurrencyNeedsConversion');
      }
      return t('bulkRebalance:insights.multiCurrencyAccount');
    }

    case OptimizerDecisionStage.RebalanceMode: {
      if (ZERO_DEPLOY_CASH_MODE_PATTERN.test(action)) {
        return t('bulkRebalance:insights.cashDeployBlockedMultiCurrency');
      }
      if (ZERO_CASH_MODE_PATTERN.test(action)) {
        return t('bulkRebalance:insights.cashRaiseBlockedMultiCurrency');
      }

      const raised = action.match(CASH_RAISE_HOLDINGS_PATTERN);
      if (raised) {
        const cents = Number(raised[1]);
        const count = Number(raised[2]);
        const source = resolveCashModeInsightSource({
          kind: 'raise',
          cents,
          count,
          audit,
          diffRows: context.diffRows,
          subAccountsById,
          usedSubAccountIds: context.usedCashModeSubAccountIds,
        });

        if (source) {
          return t('bulkRebalance:insights.cashRaiseAppliedFrom', {
            amount: formatMoneyValue(cents),
            holding: source.holdingLabels.join(', '),
            account: source.accountLabel,
          });
        }

        return t('bulkRebalance:insights.cashRaiseApplied', {
          amount: formatMoneyValue(cents),
          count,
        });
      }

      const deployed = action.match(CASH_DEPLOY_HOLDINGS_PATTERN);
      if (deployed) {
        const cents = Number(deployed[1]);
        const count = Number(deployed[2]);
        const source = resolveCashModeInsightSource({
          kind: 'deploy',
          cents,
          count,
          audit,
          diffRows: context.diffRows,
          subAccountsById,
          usedSubAccountIds: context.usedCashModeSubAccountIds,
        });

        if (source) {
          return t('bulkRebalance:insights.cashDeployAppliedFrom', {
            amount: formatMoneyValue(cents),
            holding: source.holdingLabels.join(', '),
            account: source.accountLabel,
          });
        }

        return t('bulkRebalance:insights.cashDeployApplied', {
          amount: formatMoneyValue(cents),
          count,
        });
      }

      return action.replace(/(\d+)c\b/g, (_, cents) =>
        formatMoneyValue(Number(cents)),
      );
    }

    default:
      return action;
  }
};

export const buildSubAccountNamesById = (
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
  audit?: OptimizerAuditV2Record,
) => {
  const names = new Map(subAccountsById);

  for (const result of audit?.subAccountResults ?? []) {
    const subAccountId = result.subAccountId;
    if (!subAccountId || names.has(subAccountId)) {
      continue;
    }

    names.set(subAccountId, {
      id: subAccountId,
      name: formatAuditAccountType(result.accountType),
      accountType: formatAuditAccountType(result.accountType),
      inactive: false,
      taxRank: 0,
      cashCents: 0,
      expectedCashCents: 0,
      cashOnHoldToTradeCents: 0,
      pendingWithdrawCents: 0,
      pendingContributionCents: 0,
      currency: CurrencyCodes.CAD,
      accountBaseCurrency: CurrencyCodes.CAD,
    });
  }

  return names;
};
